import { useMemo } from 'react'
import { createColumnHelper } from '@atbay/cyber-components'
import Show from '@/ui/components/helpers/Show'
import Typography from '@/ui/components/typography'
import { StyledCustomTable } from '@/ui/components/CustomTable/CustomTable.styles'
import TableEmpty from '@/ui/components/table/table-empty'
import { useAssetsTable } from '@routes/digital-footprint/routes/assets/components/assetsTable/useAssetsTable/useAssetsTable'
import {
    Cell,
    SeverityCell,
    IssuesCell,
    IssueCountItem,
} from '@routes/digital-footprint/routes/assets/components/assetsTable/assetsTable.styles'
import {
    getHighestSeverity,
    countIssuesBySeverity,
    SEVERITY_TEXT_MAPPING,
} from '@routes/digital-footprint/routes/assets/components/assetsTable/utils'
import SeverityIcon from '@/ui/components/svg/SeverityIcon'

const columnHelper = createColumnHelper<TAsset>()

interface UsersTableProps {
    assets: TAsset[]
    emptyUsersMessage: string
}

const UsersTable = ({ assets, emptyUsersMessage }: UsersTableProps) => {
    const { sorting, setSorting, handleRowClick, activeAssetId } =
        useAssetsTable({ assets, origin: 'users_table' })

    const columns = useMemo(
        () => [
            {
                id: 'risk',
                ...columnHelper.accessor('issues', {
                    cell: (info) => {
                        const asset = info.row.original
                        const highestSeverity = getHighestSeverity(asset.issues)

                        return (
                            <SeverityCell
                                onClick={() =>
                                    handleRowClick(info.row, info.table)
                                }
                                data-testid={`user-table-risk-${highestSeverity}`}
                            >
                                <SeverityIcon severity={highestSeverity} />
                                <Typography
                                    className="table-cell-text"
                                    variant="s-regular"
                                    testId="user-risk"
                                >
                                    {SEVERITY_TEXT_MAPPING[highestSeverity]}
                                </Typography>
                            </SeverityCell>
                        )
                    },
                    header: () => (
                        <Typography variant="s-regular">Severity</Typography>
                    ),
                    enableSorting: true,
                }),
                width: '15%',
            },
            {
                id: 'emailAddress',
                ...columnHelper.accessor('hostname', {
                    cell: (info) => {
                        const { hostname } = info.row.original

                        return (
                            <Cell
                                onClick={() =>
                                    handleRowClick(info.row, info.table)
                                }
                                data-testid="user-table-email"
                            >
                                <Typography
                                    className="table-cell-text"
                                    variant="s-regular"
                                    testId="user-email"
                                >
                                    {hostname}
                                </Typography>
                            </Cell>
                        )
                    },
                    header: () => (
                        <Typography variant="s-regular">
                            Email address
                        </Typography>
                    ),
                    enableSorting: true,
                }),
                width: '25%',
            },
            {
                id: 'name',
                ...columnHelper.accessor('firstName', {
                    cell: (info) => {
                        const { firstName, lastName } = info.row.original
                        const fullName = [firstName, lastName]
                            .filter(Boolean)
                            .join(' ')

                        return (
                            <Cell
                                onClick={() =>
                                    handleRowClick(info.row, info.table)
                                }
                                data-testid="user-table-name"
                            >
                                <Typography
                                    className="table-cell-text"
                                    variant="s-regular"
                                    testId="user-name"
                                >
                                    {fullName}
                                </Typography>
                            </Cell>
                        )
                    },
                    header: () => (
                        <Typography variant="s-regular">Name</Typography>
                    ),
                    enableSorting: true,
                }),
                width: '20%',
            },
            {
                id: 'openIssues',
                ...columnHelper.accessor('issues', {
                    cell: (info) => {
                        const asset = info.row.original
                        const issuesCount = countIssuesBySeverity(asset.issues)

                        const severityLevels: Array<
                            Exclude<TIssueSeverity, 'clean'>
                        > = ['critical', 'important', 'moderate']

                        return (
                            <IssuesCell
                                onClick={() =>
                                    handleRowClick(info.row, info.table)
                                }
                                data-testid="user-table-open-issues"
                            >
                                {severityLevels.map((severity) => (
                                    <Show
                                        key={severity}
                                        when={issuesCount?.[severity]}
                                    >
                                        <IssueCountItem
                                            className={`count ${severity}`}
                                            data-testid="user-issues-severity-count"
                                        >
                                            <SeverityIcon
                                                severity={severity}
                                                dataTestId={`severity-${severity}`}
                                            />
                                            <Typography
                                                className="table-cell-text"
                                                variant="s-regular"
                                            >
                                                {issuesCount?.[severity]}
                                            </Typography>
                                        </IssueCountItem>
                                    </Show>
                                ))}
                            </IssuesCell>
                        )
                    },
                    header: () => (
                        <Typography variant="s-regular">Open Issues</Typography>
                    ),
                    enableSorting: true,
                }),
                width: '20%',
            },
        ],
        [handleRowClick]
    )

    return (
        <StyledCustomTable
            activeRow={activeAssetId}
            getRowId={(row: TAsset) => row.id}
            tableHeight={700}
            columns={columns}
            data={assets}
            sorting={sorting}
            setSorting={setSorting}
            emptyState={<TableEmpty message={emptyUsersMessage} />}
        />
    )
}

export default UsersTable
